import React from 'react'

function Slider({ id, label, value, min, max, step, onChange, display, baseline }) {
  return (
    <div className="be-ctl">
      <div className="be-ctl-top">
        <label htmlFor={id}>{label}</label>
        <span className="be-val">{display}</span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      {baseline && <span className="be-base">{baseline}</span>}
    </div>
  )
}

function CostRow({ line, onToggle, onAmount }) {
  return (
    <div className={`be-cost${line.enabled ? '' : ' be-off'}`}>
      <input
        type="checkbox"
        checked={line.enabled}
        onChange={(e) => onToggle(line.id, e.target.checked)}
        aria-label={`Include ${line.name}`}
      />
      <span className="be-cost-nm">
        {line.name}
        {line.note && <em>{line.note}</em>}
      </span>
      <span className="be-amt-field">
        <span className="be-dollar">$</span>
        <input
          type="number"
          min="0"
          step="1"
          value={line.amount}
          disabled={!line.enabled}
          onChange={(e) => onAmount(line.id, e.target.value)}
          aria-label={`${line.name} dollars per quarter`}
        />
      </span>
    </div>
  )
}

const money = (n) => `$${Math.round(n).toLocaleString()}`
const pct = (n, d = 0) => `${(n * 100).toFixed(d)}%`

function BreakEvenInputs({ settings, baselines, onChange, onReset, fixedTotal }) {
  const set = (patch) => onChange({ ...settings, ...patch })

  const toggleCost = (id, enabled) =>
    set({ costLines: settings.costLines.map((c) => (c.id === id ? { ...c, enabled } : c)) })

  const setAmount = (id, raw) => {
    const amount = raw === '' ? 0 : Math.max(0, Number(raw))
    if (Number.isNaN(amount)) return
    set({ costLines: settings.costLines.map((c) => (c.id === id ? { ...c, amount } : c)) })
  }

  return (
    <aside className="be-rail">
      <div className="be-grp">
        <div className="be-eyebrow">Coach wage calibration</div>
        <Slider
          id="be-calib"
          label="Modeled → actual factor"
          value={settings.calibration}
          min={0.4}
          max={1}
          step={0.01}
          display={settings.calibration.toFixed(2)}
          baseline="books Mar–May 2026: 0.57 · uncalibrated: 1.00"
          onChange={(calibration) => set({ calibration })}
        />
        <Slider
          id="be-rate"
          label="Coach hourly rate"
          value={settings.coachRate}
          min={16}
          max={40}
          step={0.5}
          display={`$${settings.coachRate.toFixed(2).replace(/\.00$/, '')}`}
          baseline="PayrollService default $21"
          onChange={(coachRate) => set({ coachRate })}
        />
        <Slider
          id="be-cburden"
          label="Coach payroll burden"
          value={settings.coachBurden}
          min={0}
          max={0.35}
          step={0.0025}
          display={pct(settings.coachBurden, 2)}
          baseline="FICA 7.65 + FUTA/SUI 2.5 + ETT 0.1 + WC ~4"
          onChange={(coachBurden) => set({ coachBurden })}
        />
      </div>

      <div className="be-grp">
        <div className="be-eyebrow">Program drivers</div>
        <Slider
          id="be-mix"
          label="Summer camp mix"
          value={settings.summerCampMix}
          min={0}
          max={1}
          step={0.01}
          display={pct(settings.summerCampMix)}
          baseline={`summer ${baselines.latestSummerLabel}: ${pct(baselines.observedCampMix)}`}
          onChange={(summerCampMix) => set({ summerCampMix })}
        />
        <Slider
          id="be-shirt"
          label="Camp shirt + award / kid"
          value={settings.campShirtPerKid}
          min={0}
          max={30}
          step={1}
          display={money(settings.campShirtPerKid)}
          baseline="FDD requires one per camp participant"
          onChange={(campShirtPerKid) => set({ campShirtPerKid })}
        />
        <div className="be-derived">
          <div><span>Enrollment</span><b>{baselines.class.enrollment.toFixed(1)} class · {baselines.camp ? baselines.camp.enrollment.toFixed(1) : '—'} camp</b></div>
          <div><span>Price</span><b>{money(baselines.class.price)} · {baselines.camp ? money(baselines.camp.price) : '—'}</b></div>
          <div><span>City rev share</span><b>{pct(baselines.revShare, 1)}</b></div>
          <div><span>Cancellation</span><b>{pct(baselines.cancelRate)}</b></div>
          <em>Live from the API — not editable</em>
        </div>
      </div>

      <div className="be-grp">
        <div className="be-eyebrow">Manager wages — fixed</div>
        <Slider
          id="be-mgrn" label="People" value={settings.managerCount}
          min={0} max={6} step={1} display={settings.managerCount}
          onChange={(managerCount) => set({ managerCount })}
        />
        <Slider
          id="be-mgrh" label="Hours / week each" value={settings.managerHoursPerWeek}
          min={0} max={40} step={1} display={settings.managerHoursPerWeek}
          onChange={(managerHoursPerWeek) => set({ managerHoursPerWeek })}
        />
        <Slider
          id="be-mgrr" label="Hourly rate" value={settings.managerRate}
          min={15} max={60} step={1} display={money(settings.managerRate)}
          onChange={(managerRate) => set({ managerRate })}
        />
        <div className="be-derived be-tight">
          <div>
            <span>Cost / quarter</span>
            <b>{money(settings.managerCount * settings.managerHoursPerWeek * settings.managerRate * 13 * (1 + settings.managerBurden))}</b>
          </div>
        </div>
      </div>

      <div className="be-grp">
        <div className="be-eyebrow">Franchise terms — FDD</div>
        <Slider
          id="be-roy" label="Royalty rate" value={settings.royaltyRate}
          min={0} max={0.15} step={0.005} display={pct(settings.royaltyRate, 1)}
          baseline="FDD: greater of 9% or the minimum"
          onChange={(royaltyRate) => set({ royaltyRate })}
        />
        <Slider
          id="be-min" label="Monthly minimum" value={settings.royaltyMinMonthly}
          min={0} max={2500} step={50} display={money(settings.royaltyMinMonthly)}
          baseline="$1,100 paid = $900 royalty + $200 tech fee"
          onChange={(royaltyMinMonthly) => set({ royaltyMinMonthly })}
        />
        <label className="be-check">
          <input
            type="checkbox"
            checked={settings.yearlyReconciliation}
            onChange={(e) => set({ yearlyReconciliation: e.target.checked })}
          />
          <span>Yearly reconciliation</span>
          <em>lets summer absorb the school-year shortfall</em>
        </label>
        <label className="be-check">
          <input
            type="checkbox"
            checked={settings.royaltyOnGross}
            onChange={(e) => set({ royaltyOnGross: e.target.checked })}
          />
          <span>Royalty on gross revenue</span>
          <em>FDD says “Gross Revenue” but never defines it against the city split</em>
        </label>
      </div>

      <div className="be-grp">
        <div className="be-eyebrow">Advertising</div>
        <Slider
          id="be-adrate" label="Contractual floor" value={settings.localAdRate}
          min={0} max={0.08} step={0.005} display={pct(settings.localAdRate, 1)}
          baseline="FDD requires ≥3% of gross revenue"
          onChange={(localAdRate) => set({ localAdRate })}
        />
        <div className="be-cost">
          <span className="be-cost-nm" style={{ gridColumn: '1 / 3' }}>
            Budget / quarter<em>lifted to the floor when revenue grows</em>
          </span>
          <span className="be-amt-field">
            <span className="be-dollar">$</span>
            <input
              type="number" min="0" step="1" value={settings.adBudgetPerQuarter}
              onChange={(e) => set({ adBudgetPerQuarter: Math.max(0, Number(e.target.value) || 0) })}
              aria-label="Advertising budget per quarter"
            />
          </span>
        </div>
      </div>

      <div className="be-grp">
        <div className="be-eyebrow">Fixed costs — per quarter</div>
        {settings.costLines.map((line) => (
          <CostRow key={line.id} line={line} onToggle={toggleCost} onAmount={setAmount} />
        ))}
        <div className="be-tot">
          <span>Fixed / school-year qtr</span>
          <span className="be-num">{money(fixedTotal)}</span>
        </div>
        <button type="button" className="be-reset" onClick={onReset}>
          Reset to book values
        </button>
      </div>
    </aside>
  )
}

export default BreakEvenInputs
